import { fontsServiceInstance, metadataServiceInstance } from "@/services";
import { NS } from "@/common/namespaces";
import { getConsolidatedTransformMatrix } from '@/common/utilities';
import { IllustratorJustifcation, IllustratorTextType } from "@/common/enums";
import { htmlToSvgMappings } from "./text-editor-common";
import { remapHtmlToMetadata } from './html-to-metadata';

const domParser = new DOMParser();

interface ITSpanParagraphGroup {
    width: number,
    elements: SVGTSpanElement[],
}

export function rawHtmlToSvg(owningTextElement: SVGTextElement, rawHtml: string) {
    const textBoxMetadata = metadataServiceInstance.getMetadataForElement(owningTextElement);
    const parsedDom = domParser.parseFromString(rawHtml, "text/html");

    const requiredFonts = new Set<string>();
    const allTSpanParagraphGroups: ITSpanParagraphGroup[] = [];
    const defaultLineHeight = Number(owningTextElement.getAttribute("pe-lineheight") ?? "1.2");

    let originalTspanOffset: number = 0;
    if(textBoxMetadata?.element_positional_info) {
        const elMatSvg = getConsolidatedTransformMatrix(owningTextElement);
        const elMatRO = DOMMatrixReadOnly.fromMatrix(elMatSvg);

        const elX = owningTextElement.x.baseVal[0]?.value ?? 0;
        const elY = owningTextElement.y.baseVal[0]?.value ?? 0;

        const elTruePosition = elMatRO.transformPoint(new DOMPoint(elX, elY));
        originalTspanOffset = textBoxMetadata.element_positional_info.left - elTruePosition.x;
    }

    remapHtmlToMetadata(owningTextElement, Array.from(parsedDom.body.children));
    owningTextElement.replaceChildren(); // Clears out the selected element

    let firstParagraph = true;
    let maxParagraphWidth = 0;

    for (const parsedChild of parsedDom.body.children) {
        const convertedTspans = htmlToSvg(owningTextElement, parsedChild as HTMLElement);

        if (convertedTspans.length === 0) {
            convertedTspans.push(document.createElementNS(NS.SVG, "tspan"));
        }

        // Group convertedTspans by the presence of an 'new-para' attribute.
        const tspanParagraphGroups: ITSpanParagraphGroup[] = [];
        let currentParagraphGroup: SVGTSpanElement[] = [];

        for(const t of convertedTspans) {
            if(t.hasAttribute("new-para")) {
                const paragraphWidth = calcTspanCollectionWidth(owningTextElement, currentParagraphGroup);
                maxParagraphWidth = Math.max(maxParagraphWidth, paragraphWidth);

                const group = {
                    width: paragraphWidth,
                    elements: currentParagraphGroup,
                };

                tspanParagraphGroups.push(group);
                allTSpanParagraphGroups.push(group);
                currentParagraphGroup = [];

                t.removeAttribute("new-para");
            }
            currentParagraphGroup.push(t);
        }

        if(currentParagraphGroup.length > 0) {
            const paragraphWidth = calcTspanCollectionWidth(owningTextElement, currentParagraphGroup);
            maxParagraphWidth = Math.max(maxParagraphWidth, paragraphWidth);

            const group = {
                width: paragraphWidth,
                elements: currentParagraphGroup,
            };

            tspanParagraphGroups.push(group);
            allTSpanParagraphGroups.push(group);
        }

        for(const group of tspanParagraphGroups) {
            const first = group.elements[0];
            const localLineHeight = Number(first.getAttribute("line-height") ?? defaultLineHeight);
            first.setAttribute("x", "0");
            first.setAttribute("dy", firstParagraph ? `0em` : `${localLineHeight}em`);

            let hasSetFill = false;
            for (const t of group.elements) {
                if (t) {
                    if(!hasSetFill && t.hasAttribute("fill")) {
                        owningTextElement.setAttribute("fill", t.getAttribute("fill") ?? "");
                        hasSetFill = true;
                    }

                    if (t.hasAttribute("font-family")) {
                        const fontFamily = t.getAttribute("font-family")?.trim() ?? "";
                        if (fontFamily.length > 0) {
                            requiredFonts.add(fontFamily);
                        }
                    }

                    owningTextElement.appendChild(t);
                }
            }

            firstParagraph = false;
        }
    }

    for (const fnt of requiredFonts) {
        fontsServiceInstance.AddFontDependency(fnt);
    }

    if(textBoxMetadata?.artRotatedbounds) {
        maxParagraphWidth = textBoxMetadata.artRotatedbounds.right - textBoxMetadata.artRotatedbounds.left;
    }

    for(const group of allTSpanParagraphGroups) {
        const paragraphJustification = textBoxMetadata?.paragraph_metadata?.[0]?.ParagraphJustification ?? IllustratorJustifcation.Left;
        let offset = 0;
        switch (paragraphJustification) {
            default:
            case IllustratorJustifcation.Left:
            case IllustratorJustifcation.FullJustifyLastLineLeft: // TODO
            case IllustratorJustifcation.FullJustifyLastLineFull: // TODO
                // Do nothing, our tspan's are left aligned by default
                continue;

            case IllustratorJustifcation.Right:
            case IllustratorJustifcation.FullJustifyLastLineRight: // TODO
                offset = maxParagraphWidth - group.width;
                break;

            case IllustratorJustifcation.Center:
            case IllustratorJustifcation.FullJustifyLastLineCenter: // TODO
                offset = (maxParagraphWidth - group.width) / 2;
                break;
        }

        //console.log(`Max width: ${maxParagraphWidth}, Group width: ${group.width}, Offset: ${offset}`);
            
        for(const t of group.elements) {
            if(t.hasAttribute("x")) {
                const currentX = Number(t.getAttribute("x"));
                t.setAttribute("x", `${currentX + offset + originalTspanOffset}`);
            }
        }
    }
}

export function htmlToSvg(textBox: SVGTextElement, htmlElement: HTMLElement): SVGTSpanElement[] {
    const finalTSpans: SVGTSpanElement[] = [];
    const parsedElements = htmlToSvgRecursive(htmlElement, []);

    let textBoxBBox: { width: number; } = null;
    const textBoxMetadata = metadataServiceInstance.getMetadataForElement(textBox);
    if(textBoxMetadata) {
        textBoxBBox = textBoxMetadata.element_positional_info;
    }

    if(!textBoxBBox) {
        // Text box has no width, so return the spans as-is.
        return parsedElements;
    }

    let previousParaLength = 0;
    let isNewPara = false;
    for(const el of parsedElements) {
        const splText = el.textContent.split(/\s+/);

        while(splText.length > 0) {
            const finalTspan = el.cloneNode(true) as SVGTSpanElement;
            if(isNewPara) {
                finalTspan.setAttribute("new-para", "true");
                isNewPara = false;
                previousParaLength = 0;
            }
            textBox.appendChild(finalTspan);
            let wordCount = 0;
            
            if(textBoxMetadata.text_element_type === IllustratorTextType.PointText) {
                wordCount = splText.length;
            } else {
                while(wordCount < splText.length) {
                    finalTspan.textContent = splText.slice(0, wordCount + 1).join(" ");

                    const testBBox = finalTspan.getBBox();
                    if (testBBox.width + previousParaLength > textBoxBBox.width) {
                        isNewPara = true;
                        break;
                    }

                    ++wordCount;
                }
            }

            // If it's impossible to wrap, let the word break out of the box.
            wordCount = Math.max(wordCount, 1);

            finalTspan.textContent = splText.slice(0, wordCount).join(" ");
            previousParaLength += finalTspan.getBBox().width;
            textBox.removeChild(finalTspan);
            finalTSpans.push(finalTspan);
            splText.splice(0, wordCount);
        }
    }

    return finalTSpans;
}

function htmlToSvgRecursive(el: HTMLElement, parentNodeStack: HTMLElement[]): SVGTSpanElement[] {
    const parsedElements: SVGTSpanElement[] = [];
    parentNodeStack = [...parentNodeStack, el];

    for (const child of el.childNodes) {
        if (child.nodeType === Node.ELEMENT_NODE) {
            for (const p of htmlToSvgRecursive(child as HTMLElement, parentNodeStack)) {
                if (p) {
                    parsedElements.push(p);
                }
            }
        } else if (child.nodeType === Node.TEXT_NODE) {
            const tspan = document.createElementNS(NS.SVG, "tspan");
            tspan.textContent = child.textContent;

            for (const parent of parentNodeStack) {
                if('attributeStyleMap' in parent) {
                    for (const [attr, val] of parent.attributeStyleMap) {
                        let attrib = attr;
                        if(attrib in htmlToSvgMappings) {
                            attrib = htmlToSvgMappings[attr];
                        }

                        tspan.setAttribute(attrib, val.toString());
                    }
                } else {
                    for (const attr of (parent as HTMLElement).style) {
                        let attrib = attr;
                        if(attrib in htmlToSvgMappings) {
                            attrib = htmlToSvgMappings[attr];
                        }

                        tspan.setAttribute(attrib, (parent as HTMLElement).style[attr].toString());
                    }
                }
            }

            parsedElements.push(tspan);
        }
    }

    return parsedElements;
}

function calcTspanCollectionWidth(parentElement: SVGTextElement, collection: SVGTSpanElement[]) {
    let length = 0;

    for (const t of collection) {
        parentElement.appendChild(t);
        length += t.getComputedTextLength();
        parentElement.removeChild(t);
    }

    return length;
}